import clsx from 'clsx';
import React, { useContext, useId } from 'react';
import { Label as LabelComponent } from '@inpart-io/react-components';
import * as DragDrop from './DragDrop';
import * as File from './File';

type InputCtx = {
  id: string;
};

const InputCtx = React.createContext<InputCtx>({
  id: '',
});

function useInputId() {
  const { id } = useContext(InputCtx);
  const fallbackId = useId();
  return id || fallbackId;
}

const commonStyles = `block font-light mb-1`;

export interface InputGroupProps extends React.ComponentPropsWithRef<'div'> {
  id?: string;
}

const InputGroup: React.FC<InputGroupProps> = props => {
  const { id: providedId, ...other } = props;
  const generatedId = useId();
  return (
    <InputCtx.Provider value={{ id: providedId || generatedId }}>
      <div className="mb-6" {...other}>
        {props.children}
      </div>
    </InputCtx.Provider>
  );
};

const Label: React.FC<React.ComponentProps<'label'>> = props => {
  const id = useInputId();
  const { children, ...other } = props;
  return (
    <label htmlFor={id} className="inline-block font-bold text-base mb-2" {...other}>
      {children}
    </label>
  );
};

export interface FieldProps extends React.ComponentPropsWithRef<'input'> {
  disabled?: boolean;
}

const Field: React.FC<FieldProps> = ({ className, ...props }) => {
  const id = useInputId();
  const { disabled, type = 'text', ...other } = props;
  return (
    <input
      type={type}
      id={id}
      className={clsx(commonStyles, `py-1 px-4 rounded-lg border border-grey-light`, className)}
      disabled={disabled}
      {...other}
    />
  );
};

export interface TextAreaProps extends React.ComponentPropsWithRef<'textarea'> {
  disabled?: boolean;
}

const TextArea: React.FC<TextAreaProps> = props => {
  const id = useInputId();
  const { disabled, children, ...other } = props;
  return (
    <textarea
      id={id}
      className={clsx(commonStyles, `py-2 px-4 rounded-lg border border-grey-light`)}
      disabled={disabled}
      style={{
        scrollbarWidth: 'none',
        resize: 'none',
        msOverflowStyle: 'none',
      }}
      {...other}
    >
      {children}
    </textarea>
  );
};

export interface CheckboxProps extends React.ComponentPropsWithRef<'input'> {
  label?: string | React.ReactNode;
  labelClassName?: string;
}

const Checkbox: React.FC<CheckboxProps> = props => {
  const id = useInputId();
  const { label, disabled, labelClassName, className, ...other } = props;
  return (
    <label className={clsx(labelClassName, 'flex items-start mb-1')}>
      <input
        id={id}
        type="checkbox"
        className={clsx('h-5 w-5 rounded-lg border-3 border-gray-400 mt-0.5', className)}
        disabled={disabled}
        {...other}
      />
      <span className={clsx(`ml-2 text-gray-700`, disabled && `text-gray-400`)}>{label}</span>
    </label>
  );
};

export interface RadioProps extends React.ComponentPropsWithRef<'input'> {
  label?: string | React.ReactNode;
}

const Radio: React.FC<RadioProps> = props => {
  const id = useInputId();
  const { label, disabled, ...other } = props;
  return (
    <label className="flex items-start mb-1">
      <input
        id={id}
        type="radio"
        className="h-5 w-5 border border-action-outline-secondary-enabled shadow-light-down mt-0.5"
        disabled={disabled}
        {...other}
      />
      <span className={clsx(`ml-2 text-gray-700`, disabled && `text-gray-400`)}>{label}</span>
    </label>
  );
};

export interface SwitchProps extends React.ComponentPropsWithRef<'input'> {
  label?: string | React.ReactNode;
  sizeVariant?: 'sm' | 'md';
  labelPosition?: 'left' | 'right';
  labelClassName?: string;
}

const Switch: React.FC<SwitchProps> = props => {
  const id = useInputId();
  const { label, disabled, sizeVariant = 'md', className, labelPosition = 'right', labelClassName, ...other } = props;

  // Size variants configuration
  const sizeVariants = {
    sm: {
      track: 'w-7.5 h-5',
      toggle: 'w-[15px] h-[15px] left-0.5 top-[2.7px]',
      checkedTransform: 'peer-checked:translate-x-[12px]',
    },
    md: {
      track: 'w-10 h-6',
      toggle: 'w-4 h-4 left-1 top-1',
      checkedTransform: 'peer-checked:translate-x-full',
    },
  };

  const currentSize = sizeVariants[sizeVariant];

  const switchElement = (
    <div
      className={clsx(
        'relative',
        !disabled &&
          'hover:[&>.track]:bg-interaction-outline-hover hover:[&>input:checked_~_.track]:bg-action-fill-primary-hover',
      )}
    >
      {/* Underlying form control */}
      <input id={id} type="checkbox" className={clsx('peer sr-only', className)} disabled={disabled} {...other} />

      {/* Track */}
      <div
        className={clsx(
          'track block border rounded-full transition',
          currentSize.track,
          disabled
            ? 'border-gray-300 bg-gray-200 peer-checked:bg-gray-300'
            : 'border-action-outline-secondary-enabled bg-action-outline-secondary-hover peer-checked:bg-action-fill-primary-enabled',
        )}
      />

      {/* Toggle */}
      <div
        className={clsx(
          'absolute rounded-full transition drop-shadow',
          currentSize.toggle,
          currentSize.checkedTransform,
          disabled ? 'bg-gray-400' : 'bg-action-fill-secondary-enabled',
        )}
      />
    </div>
  );

  const labelElement = (
    <label className={labelClassName} htmlFor={id}>
      {label}
    </label>
  );

  return (
    <>
      <LabelComponent
        className={clsx('flex gap-x-2 items-center', disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')}
        labelSize={sizeVariant}
      >
        {labelPosition === 'left' ? (
          <>
            {labelElement}
            {switchElement}
          </>
        ) : (
          <>
            {switchElement}
            {labelElement}
          </>
        )}
      </LabelComponent>
    </>
  );
};

const Select: React.FC<React.ComponentPropsWithRef<'select'>> = props => {
  const id = useInputId();
  const { disabled, children, ...other } = props;
  return (
    <select
      id={id}
      className={clsx(
        commonStyles,
        `pl-4 pr-10 placeholder:text-content-tertiary text-body-lg text-content-primary py-[7px] px-2 shadow-light-down`,
      )}
      disabled={disabled}
      {...other}
    >
      {children}
    </select>
  );
};

const Text: React.FC<React.PropsWithChildren> = props => <small className="font-light">{props.children}</small>;

export const Input = {
  Group: InputGroup,
  Label,
  Field,
  TextArea,
  Checkbox,
  Radio,
  Switch,
  Select,
  Text,
  ...DragDrop,
  ...File,
};
